/**
 * IBKR Settings API service.
 */

import type { IbkrConnectionInput, IbkrConnectionResponse, IbkrLiveStatusResponse, IbkrStatusReason } from '@covered-calls/shared';
import { apiClient } from './api-client';

const IBKR_SETTINGS_PATH = '/settings/ibkr';
const IBKR_STATUS_PATH = '/settings/ibkr/status';

/**
 * Fetch the current IBKR connection configuration.
 * Returns null if no connection is configured.
 */
export async function getIbkrConnection(): Promise<IbkrConnectionResponse | null> {
  try {
    return await apiClient.get<IbkrConnectionResponse>(IBKR_SETTINGS_PATH);
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'status' in error) {
      const status = (error as { status: number }).status;
      if (status === 404 || status >= 500 || status === 0) {
        return null;
      }
    }
    throw error;
  }
}

/**
 * Create or update the IBKR connection configuration.
 */
export async function saveIbkrConnection(input: IbkrConnectionInput): Promise<IbkrConnectionResponse> {
  return apiClient.put<IbkrConnectionResponse>(IBKR_SETTINGS_PATH, input);
}

/**
 * Delete the IBKR connection configuration.
 */
export async function deleteIbkrConnection(): Promise<void> {
  await apiClient.delete(IBKR_SETTINGS_PATH);
}

/** Response from the test connection endpoint. */
export interface TestConnectionResult {
  success: boolean;
  message: string;
  /** True when the gateway is reachable but the IBKR session has expired and
   *  the user needs to re-authenticate at their gateway URL. */
  needsReauth?: boolean;
  /** Machine-readable cause for a failed test (e.g. GATEWAY_DOWN when ngrok is
   *  up but the IB Gateway isn't, TUNNEL_UNREACHABLE when ngrok isn't running). */
  reason?: IbkrStatusReason;
}

/**
 * Tests the IBKR connection by verifying the gateway is authenticated.
 */
export async function testIbkrConnection(): Promise<TestConnectionResult> {
  return apiClient.post<TestConnectionResult>(IBKR_SETTINGS_PATH, {});
}

/**
 * Performs a live IBKR connection check via the dedicated status endpoint.
 *
 * Lets {@link ApiClientError} propagate to the caller (the hook) on transport or
 * HTTP failures so it can present error/timeout states.
 */
export async function getIbkrLiveStatus(): Promise<IbkrLiveStatusResponse> {
  return apiClient.get<IbkrLiveStatusResponse>(IBKR_STATUS_PATH);
}
